// import node module libraries
import React, { Fragment, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";

import { Col, Row, Container, Card, Button, Badge, ProgressBar } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar/dist/simplebar.min.css";

// import custom components
import NavbarDefault from "layouts/marketing/navbars/NavbarDefault";
import VideoPlayer from "components/marketing/common/video/VideoPlayer";
import PdfViewer from "components/marketing/common/pdf-viewer/PdfViewer";
import SurveyViewer from "components/elements/survey-viewer/SurveyViewer";
import GKAccordionModule from "components/marketing/common/accordions/GKAccordionModule";

import { GET_COURSE_MODULES, GET_FILE_URL, GET_MODULE_BY_ID } from "helper/graphql/queries";
import { CREATE_QUIZ_ANSWER } from "helper/graphql/mutations";
import { Notify } from "notiflix";
import useAuth from "hooks/useAuth";
import { PATH_DASHBOARD } from "routes/Paths";

export const CourseResume = () => {
  const { guid } = useParams();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isReview = pathname.includes('review');

  const [contents, setContents] = useState([]);
  const [module, setModule] = useState();
  const [allModules, setModules] = useState(null);
  const [currTopic, setCurrTopic] = useState(null);
  const [currTopicFile, setCurrTopicFile] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [getFile] = useLazyQuery(GET_FILE_URL);
  const [onCreateQuizAnswer] = useMutation(CREATE_QUIZ_ANSWER);

  useQuery(GET_MODULE_BY_ID, {
    variables: { guid: guid },
    onCompleted: (data) => {
      const _module = data?.allCourseModules?.edges[0]?.node;
      const _resources = _module?.coursemoduleresourcesSet?.edges?.map(
        (item) => item.node
      )?.filter(item => item.contentType !== "links");

      const _quizes = _module?.modulecontentquizSet?.edges?.map(
        (item) => item.node
      );

      const _topics = _module?.coursemodulecontentsSet?.edges?.map(
        (item) => item.node
      );

      const _links = _module?.coursemoduleresourcesSet?.edges?.map(
        (item) => item.node
      )?.filter(item => item.contentType === "links");

      setModule(_module);
      setContents([
        {
          id: 101,
          title: "Topics",
          items: _topics,
          disabled: true,
          icon: 'book-open'
        },
        {
          id: 102,
          title: "Resources",
          items: _resources,
          disabled: false,
          icon: 'file-text'
        },
        {
          id: 103,
          title: "Quizzes",
          items: _quizes,
          disabled: false,
          icon: 'help-circle'
        },
        {
          id: 104,
          title: "Links",
          items: _links,
          disabled: false,
          icon: 'link'
        },
      ]);

      const topic = _resources[0];
      setCurrTopic(topic);
      if (topic?.contentType && topic.contentType === "file") {
        handleGetFileURL(topic?.file);
      } else {
        setCurrTopicFile(null);
      }
    },
  });

  useQuery(GET_COURSE_MODULES, {
    variables: { courseGuid: module?.course?.guid },
    onCompleted: (data) => {
      setModules(data?.allCourseModules?.edges?.map(mod => mod?.node));
    }
  });

  const getNextModule = (modules, currentGuid) => {
    if (modules?.length !== 0 && currentGuid) {
      const currIndex = modules?.findIndex(mod => mod.guid === currentGuid);
      if (currIndex >= 0 && (currIndex + 1) < modules?.length) {
        return modules[currIndex + 1];
      }
      return null;
    } else {
      return null;
    }
  };

  const getPrevModule = (modules, currentGuid) => {
    if (modules?.length !== 0 && currentGuid) {
      const currIndex = modules?.findIndex(mod => mod.guid === currentGuid);
      if (currIndex > 0) {
        return modules[currIndex - 1];
      }
      return null;
    } else {
      return null;
    }
  };

  const handleNextLecture = () => {
    const _next = getNextModule(allModules, guid);
    if (_next !== null) {
      navigate(PATH_DASHBOARD.courses.resume(_next.guid));
    }
  };

  const handlePreviousLecture = () => {
    const _next = getPrevModule(allModules, guid);
    if (_next !== null) {
      navigate(PATH_DASHBOARD.courses.resume(_next.guid));
    }
  };

  const handleGetFileURL = (filename) => {
    getFile({
      variables: {
        "fileName": filename
      },
      onError: () => {
        Notify.failure("Failed to get file");
      },
      onCompleted: (data) => {
        setCurrTopicFile(data?.getFileURL);
      }
    });
  };

  const handleTopicChange = (topic) => {
    setCurrTopic(topic);
    if (topic.contentType && topic.contentType === "file") {
      handleGetFileURL(topic?.file);
    } else {
      setCurrTopicFile(null);
    }
  };

  const handleQuizComplete = (quizGUID, data) => {
    if (!isReview) {
      const payload = {
        "quizQuestionId": quizGUID,
        "userId": user?.guid,
        "isInstructorAnswer": false,
        "answerData": JSON.stringify(data)
      };

      onCreateQuizAnswer({
        variables: payload,
        onCompleted: () => {
          Notify.success('Quiz saved successfully!');
          window.location.reload();
        },
        onError: () => Notify.failure('Failed to save quiz!'),
      });
    }
  };

  // Calculate progress
  const getCurrentModuleIndex = () => {
    if (!allModules || !guid) return 0;
    return allModules.findIndex(mod => mod.guid === guid) + 1;
  };

  const getProgressPercentage = () => {
    if (!allModules || allModules.length === 0) return 0;
    return (getCurrentModuleIndex() / allModules.length) * 100;
  };

  return (
    <Fragment>
      <NavbarDefault login />
      
      <main className="course-viewer-main">
        {/* Top Navigation Bar */}
        <div className="course-topbar">
          <Container fluid>
            <div className="topbar-content">
              <div className="topbar-left">
                <Button 
                  variant="" 
                  className="back-btn"
                  onClick={() => navigate(PATH_DASHBOARD.courses.browse)}
                >
                  <i className="fe fe-arrow-left me-2"></i>
                  Back to Courses
                </Button>
                <div className="course-title-section">
                  <h5 className="course-title mb-1">{module?.course?.title}</h5>
                  <div className="course-meta">
                    <span className="meta-item">
                      <i className="fe fe-layers me-1"></i>
                      Lecture {getCurrentModuleIndex()} of {allModules?.length || 0}
                    </span>
                    <span className="meta-divider">•</span>
                    <span className="meta-item">
                      <i className="fe fe-book-open me-1"></i>
                      {module?.title}
                    </span>
                  </div>
                </div>
              </div>
              <div className="topbar-right">
                <Button
                  variant=""
                  className="sidebar-toggle-btn"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                >
                  <i className={`fe fe-${sidebarCollapsed ? 'menu' : 'x'}`}></i>
                </Button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="progress-bar-container">
              <ProgressBar 
                now={getProgressPercentage()} 
                className="course-progress"
              />
              <span className="progress-text">
                {Math.round(getProgressPercentage())}% Complete
              </span>
            </div>
          </Container>
        </div>

        {/* Main Content Area */}
        <div className="course-content-wrapper">
          <Container fluid className="h-100">
            <Row className="h-100 g-0">
              {/* Video/Content Area */}
              <Col lg={sidebarCollapsed ? 12 : 9} className="content-column">
                <div className="content-area">
                  {/* Lecture Header */}
                  <div className="lecture-header">
                    <div className="lecture-info">
                      <Badge bg="" className="lecture-type-badge">
                        {currTopic?.contentType === 'video' ? (
                          <><i className="fe fe-video me-2"></i>Video Lesson</>
                        ) : currTopic?.contentType === 'file' ? (
                          <><i className="fe fe-file-text me-2"></i>Document</>
                        ) : currTopic?.questionData ? (
                          <><i className="fe fe-help-circle me-2"></i>Quiz</>
                        ) : (
                          <><i className="fe fe-book-open me-2"></i>Content</>
                        )}
                      </Badge>
                      <h3 className="lecture-title">{currTopic?.title || 'Select content from sidebar'}</h3>
                      {currTopic?.description && (
                        <p className="lecture-description">{currTopic.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Content Display */}
                  <div className="content-display">
                    {currTopic?.contentType ? (
                      currTopic.contentType === "file" || currTopic.contentType === "image" ? (
                        <div className="document-viewer">
                          {currTopic !== null && currTopicFile && (
                            <PdfViewer doc={currTopicFile} />
                          )}
                        </div>
                      ) : currTopic.contentType === "video" ? (
                        <div className="video-viewer">
                          <VideoPlayer
                            title={currTopic?.title}
                            video={currTopic?.video || ""}
                          />
                        </div>
                      ) : null
                    ) : currTopic?.questionData ? (
                      <div className="quiz-viewer">
                        <div className="quiz-container">
                          <SurveyViewer
                            questions={currTopic.questionData}
                            onComplete={(data) => handleQuizComplete(currTopic.guid, data)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="empty-content">
                        <i className="fe fe-book-open empty-icon"></i>
                        <h4>Select Content</h4>
                        <p>Choose a topic, resource, or quiz from the sidebar to begin</p>
                      </div>
                    )}
                  </div>

                  {/* Navigation Footer */}
                  <div className="lecture-navigation">
                    <Button
                      variant=""
                      className="nav-btn nav-prev"
                      disabled={getPrevModule(allModules, guid) === null}
                      onClick={handlePreviousLecture}
                    >
                      <i className="fe fe-chevron-left me-2"></i>
                      Previous Lecture
                    </Button>
                    <Button
                      variant=""
                      className="nav-btn nav-next"
                      disabled={getNextModule(allModules, guid) === null}
                      onClick={handleNextLecture}
                    >
                      Next Lecture
                      <i className="fe fe-chevron-right ms-2"></i>
                    </Button>
                  </div>
                </div>
              </Col>

              {/* Sidebar */}
              <Col lg={3} className={`sidebar-column ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="course-sidebar">
                  <SimpleBar style={{ maxHeight: "calc(100vh - 180px)" }}>
                    <Card className="sidebar-card">
                      <Card.Header className="sidebar-header">
                        <h5 className="mb-0">
                          <i className="fe fe-list me-2"></i>
                          Course Content
                        </h5>
                      </Card.Header>
                      <GKAccordionModule
                        accordionItems={contents}
                        onSelected={handleTopicChange}
                        currTopic={currTopic}
                      />
                    </Card>
                  </SimpleBar>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </main>

      <style jsx>{`
        /* Main Layout */
        .course-viewer-main {
          background: #f8f9fa;
          min-height: 100vh;
          padding-top: 70px;
        }

        /* Top Navigation Bar */
        .course-topbar {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          padding: 1rem 0 0.5rem;
          position: fixed;
          top: 70px;
          left: 0;
          right: 0;
          z-index: 100;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .topbar-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .topbar-left {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex: 1;
        }

        .back-btn {
          background: transparent;
          border: none;
          color: #64748b;
          font-weight: 600;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          transition: all 0.3s ease;
        }

        .back-btn:hover {
          background: #f1f5f9;
          color: #F68521;
        }

        .course-title-section {
          flex: 1;
        }

        .course-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .course-meta {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #64748b;
        }

        .meta-item {
          display: flex;
          align-items: center;
        }

        .meta-divider {
          color: #cbd5e1;
        }

        .sidebar-toggle-btn {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background: #f1f5f9;
          border: none;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .sidebar-toggle-btn:hover {
          background: #F68521;
          color: white;
        }

        /* Progress Bar */
        .progress-bar-container {
          position: relative;
        }

        .course-progress {
          height: 4px;
          background: #e2e8f0;
        }

        .course-progress .progress-bar {
          background: linear-gradient(90deg, #F68521 0%, #1d9eb0 100%);
        }

        .progress-text {
          position: absolute;
          right: 0;
          top: -24px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
        }

        /* Content Wrapper */
        .course-content-wrapper {
          margin-top: 110px;
          min-height: calc(100vh - 180px);
        }

        .content-column {
          background: white;
          transition: all 0.3s ease;
        }

        .content-area {
          display: flex;
          flex-direction: column;
          height: calc(100vh - 180px);
        }

        /* Lecture Header */
        .lecture-header {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .lecture-type-badge {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.75rem;
          display: inline-flex;
          align-items: center;
        }

        .lecture-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .lecture-description {
          color: #64748b;
          margin: 0;
          line-height: 1.6;
        }

        /* Content Display */
        .content-display {
          flex: 1;
          overflow: hidden;
          background: #0f172a;
        }

        .document-viewer,
        .video-viewer,
        .quiz-viewer {
          width: 100%;
          height: 100%;
        }

        .quiz-container {
          background: white;
          height: 100%;
          overflow-y: auto;
          padding: 2rem;
        }

        .empty-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          background: white;
          color: #94a3b8;
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          opacity: 0.5;
        }

        .empty-content h4 {
          color: #64748b;
          margin-bottom: 0.5rem;
        }

        .empty-content p {
          margin: 0;
        }

        /* Navigation Footer */
        .lecture-navigation {
          display: flex;
          justify-content: space-between;
          padding: 1rem 2rem;
          border-top: 1px solid #e2e8f0;
          background: white;
        }

        .nav-btn {
          border-radius: 10px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
          border: none;
          display: flex;
          align-items: center;
        }

        .nav-prev {
          background: #f1f5f9;
          color: #64748b;
        }

        .nav-prev:hover:not(:disabled) {
          background: #e2e8f0;
          color: #1e293b;
        }

        .nav-next {
          background: linear-gradient(135deg, #F68521 0%, #ff9d4d 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.25);
        }

        .nav-next:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(246, 133, 33, 0.35);
        }

        .nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Sidebar */
        .sidebar-column {
          background: #f8f9fa;
          border-left: 1px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .sidebar-column.collapsed {
          display: none;
        }

        .course-sidebar {
        position: static;
          height: 100%;
          padding: 1rem;
        }

        .sidebar-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .sidebar-header {
          background: linear-gradient(135deg, #1d9eb0 0%, #3fc1d3 100%);
          color: white;
          border: none;
          padding: 1rem 1.25rem;
          border-radius: 12px 12px 0 0;
        }

        .sidebar-header h5 {
          color: white;
          font-weight: 700;
          display: flex;
          align-items: center;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .course-topbar {
            top: 60px;
          }

          .course-content-wrapper {
            margin-top: 100px;
          }

          .topbar-left {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
          }

          .course-title {
            font-size: 1rem;
          }

          .lecture-header {
            padding: 1rem;
          }

          .lecture-title {
            font-size: 1.25rem;
          }

          .lecture-navigation {
            padding: 0.75rem 1rem;
          }

          .nav-btn {
            padding: 0.625rem 1rem;
            font-size: 0.875rem;
          }

          .sidebar-column {
            position: fixed;
            top: 170px;
            right: 0;
            bottom: 0;
            width: 100%;
            max-width: 400px;
            z-index: 99;
            transform: translateX(0);
          }

          .sidebar-column.collapsed {
            display: block;
            transform: translateX(100%);
          }

          .content-column {
            width: 100% !important;
            max-width: 100% !important;
          }
        }

        @media (max-width: 768px) {
          .progress-text {
            display: none;
          }

          .lecture-navigation {
            flex-direction: column;
            gap: 0.5rem;
          }

          .nav-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default CourseResume;